import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './components/home/home.component';
import { ContactComponent } from './components/contact/contact.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { RegisterComponent } from './components/auth/register/register.component';
import { LoginComponent } from './components/auth/login/login.component';
import { PasswordResetComponent } from './components/auth/password-reset/password-reset.component';
import { VerifyEmailComponent } from './components/auth/verify-email/verify-email.component';
import { AboutComponent } from './components/about/about.component';
import { HowItWorksComponent } from './components/how-it-works/how-it-works.component';
import { FeaturesComponent } from './components/features/features.component';
import { AuthGuard } from './guards/auth.guard';
import { StarterComponent } from './components/starter/starter.component';
import { PlanStarterGuard } from './guards/plan-starter.guard';
import { PlanSmallFleetGuard } from './guards/plan-small-fleet.guard';
import { SmallFleetComponent } from './components/small-fleet/small-fleet.component';
import { MidFleetComponent } from './components/mid-fleet/mid-fleet.component';
import { PlanMidFleetGuard } from './guards/plan-mid-fleet.guard';
import { LargeFleetComponent } from './components/large-fleet/large-fleet.component';
import { PlanLargeFleetGuard } from './guards/plan-large-fleet.guard';
import { UpgradeSubscriptionComponent } from './components/upgrade-subscription/upgrade-subscription.component';
import { SubscriptionsComponent } from './components/subscriptions/subscriptions.component';
import { CheckoutComponent } from './components/checkout/checkout.component';
import { SubscriptionConfirmationComponent } from './components/subscription-confirmation/subscription-confirmation.component';
import { AdminGuard } from './guards/admin.guard';
import { AccountBoatRedirectGuard } from './guards/account-boat-redirect.guard';
import { AccountRoleGuard } from './guards/account-role.guard';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { UnknownComponent } from './components/unknown/unknown.component';
import { SelectedPlanGuard } from './guards/selected-plan.guard';
import { AcceptInviteComponent } from './components/accounts/accept-invite/accept-invite.component';
import { ServiceBookShareViewComponent } from './components/service-book/service-book-share-view.component';

const routes: Routes = [
  { path: '', component: HomeComponent },
  { path: 'about', component: AboutComponent },
  { path: 'affiliate', component: HomeComponent },
  { path: 'ambassadors/:referralSlug', component: HomeComponent },
  { path: 'how-it-works', component: HowItWorksComponent },
  { path: 'features', component: FeaturesComponent },
  { path: 'why-yachtapp', loadComponent: () => import('./components/why-yachtapp/why-yachtapp.component').then(m => m.WhyYachtappComponent) },
  { path: 'yachtapp-solutions', loadComponent: () => import('./components/yachtapp-solutions/yachtapp-solutions.component').then(m => m.YachtappSolutionsComponent) },
  { path: 'smart-yacht-management', loadComponent: () => import('./components/smart-yacht-management/smart-yacht-management.component').then(m => m.SmartYachtManagementComponent) },
  { path: 'overview', loadComponent: () => import('./components/overview/overview.component').then(m => m.OverviewComponent) },
  { path: 'crewfinder-details', loadComponent: () => import('./components/crewfinder-details/crewfinder-details.component').then(m => m.CrewfinderDetailsComponent) },
  { path: 'contact', component: ContactComponent },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  { path: 'terms-and-conditions', component: TermsAndConditionsComponent },
  { path: 'register', component: RegisterComponent },
  { path: 'reset-password', component: PasswordResetComponent },
  { path: 'verify-email', component: VerifyEmailComponent },
  { path: 'subscriptions', component: SubscriptionsComponent },
  { path: 'login', component: LoginComponent },
  { path: 'dashboard', data: { title: 'Dashboard' }, canActivate: [AuthGuard, AccountRoleGuard], loadChildren: () => import('./components/dashboard/dashboard.module').then((m) => m.DashboardModule) },
  { path: 'users', canActivate: [AuthGuard], loadChildren: () => import('./components/users/users.module').then((m) => m.UsersModule) },
  { path: 'users/:accountId/invites/:inviteId', component: AcceptInviteComponent },
  { path: 'service-book-share/:token', component: ServiceBookShareViewComponent },
  { path: 'settings', data: { permissions: ['admin'] }, canActivate: [AuthGuard, AccountRoleGuard], loadChildren: () => import('./components/settings/settings.module').then((m) => m.SettingsModule) },
  { path: 'billing', data: { permissions: ['billing'] }, canActivate: [AuthGuard, AccountRoleGuard], loadChildren: () => import('./components/billing/billing.module').then((m) => m.BillingModule) },
  { path: 'emergency', canActivate: [AuthGuard, AccountRoleGuard], loadChildren: () => import('./components/emergency/emergency.module').then((m) => m.EmergencyModule) },
  { path: 'activity-log', canActivate: [AuthGuard, AccountRoleGuard], loadChildren: () => import('./components/activity-log/activity-log.module').then((m) => m.ActivityLogModule) },
  { path: 'notifications', canActivate: [AuthGuard, AccountRoleGuard], loadChildren: () => import('./components/notifications/notifications.module').then((m) => m.NotificationsModule) },
  { path: 'accounts/:accountId/boats/:boatId/maintenance-schedules', canActivate: [AuthGuard, AccountBoatRedirectGuard], component: UnknownComponent, data: { maintenanceSchedules: true } },
  { path: 'accounts/:accountId/boats/:boatId', canActivate: [AuthGuard, AccountBoatRedirectGuard], component: UnknownComponent },
  { path: 'boats', canActivate: [AuthGuard, AccountRoleGuard, PlanStarterGuard], loadChildren: () => import('./components/boats/boats.module').then((m) => m.BoatsModule) },
  { path: 'checklists', canActivate: [AuthGuard, AccountRoleGuard, PlanStarterGuard], loadChildren: () => import('./components/checklists/checklists.module').then((m) => m.ChecklistsModule) },
  { path: 'inventory', canActivate: [AuthGuard, AccountRoleGuard, PlanStarterGuard], loadChildren: () => import('./components/inventory/inventory.module').then((m) => m.InventoryModule) },
  {
    path: 'cruises',
    canActivate: [AuthGuard, AccountRoleGuard, PlanStarterGuard],
    loadChildren: () => import('./components/cruises/cruises.module').then((m) => m.CruisesModule)
  },
  {
    path: 'onboard',
    canActivate: [AuthGuard, AccountRoleGuard, PlanStarterGuard],
    loadChildren: () => import('./components/onboard/onboard.module').then((m) => m.OnboardModule)
  },
  { path: 'worklogs', canActivate: [AuthGuard, AccountRoleGuard, PlanStarterGuard], loadChildren: () => import('./components/worklogs/worklogs.module').then((m) => m.WorklogsModule) },
  { path: 'reminders', canActivate: [AuthGuard, AccountRoleGuard, PlanStarterGuard], loadChildren: () => import('./components/reminders/reminders.module').then((m) => m.RemindersModule) },
  {
    path: 'nautical-map',
    canActivate: [AuthGuard, AccountRoleGuard],
    loadChildren: () => import('./components/nautical/nautical-map.module').then((m) => m.NauticalMapModule)
  },
  {
    path: 'nautical-routes',
    canActivate: [AuthGuard, AccountRoleGuard],
    loadChildren: () => import('./components/navigation-routes/navigation-routes.module').then((m) => m.NavigationRoutesModule)
  },
  { path: 'blog', canActivate: [AuthGuard], loadChildren: () => import('./components/blog/blog.module').then((m) => m.BlogModule) },
  {
    path: 'crewfinder',
    canActivate: [AuthGuard],
    loadChildren: () => import('./components/crewfinder/crewfinder.module').then((m) => m.CrewfinderModule)
  },

  { path: 'starter', component: StarterComponent, canActivate: [AuthGuard, PlanStarterGuard] },
  { path: 'small-fleet', component: SmallFleetComponent, canActivate: [AuthGuard, PlanSmallFleetGuard] },
  { path: 'mid-fleet', component: MidFleetComponent, canActivate: [AuthGuard, PlanMidFleetGuard] },
  { path: 'large-fleet', component: LargeFleetComponent, canActivate: [AuthGuard, PlanLargeFleetGuard] },

  { path: 'upgrade', component: UpgradeSubscriptionComponent, canActivate: [AuthGuard] },
  { path: 'checkout', component: CheckoutComponent, canActivate: [AuthGuard, SelectedPlanGuard] },
  { path: 'subscription-confirmation', component: SubscriptionConfirmationComponent, canActivate: [AuthGuard] },
  { path: 'thank-you', component: SubscriptionConfirmationComponent, canActivate: [AuthGuard] },

  {
    path: 'admin',
    canActivate: [AdminGuard],
    loadChildren: () => import('./components/admin/admin.module').then((m) => m.AdminModule)
  },
  { path: 'unauthorized', component: UnauthorizedComponent },

  { path: '**', component: UnknownComponent, data: { title: 'Page Not Found', description: 'The requested YachtApp page could not be found. Return to the homepage to explore the platform.' } }

];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // NavigationScrollService handles query-only updates and async anchors.
    scrollPositionRestoration: 'disabled',
    anchorScrolling: 'disabled'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
